import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { 
  ClipboardCheck,
  Package, 
  Truck,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Loader2,
  MapPin,
  Phone,
  User,
  DollarSign,
  Search,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Send,
  CreditCard,
  Sparkles,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  released: { label: 'Released', color: 'bg-purple-100 text-purple-700', icon: Package },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-cyan-100 text-cyan-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const DRIVERS = [
  { value: 'Jim Clark', label: 'Jim Clark' },
  { value: 'Lhoren', label: 'Lhoren' }
];

const NASOrderApproval = () => {
  const { user, token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modals
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isAssignDriverOpen, setIsAssignDriverOpen] = useState(false);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [isConfirmDeliveryOpen, setIsConfirmDeliveryOpen] = useState(false);
  
  // Form states
  const [rejectReason, setRejectReason] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  const [deliveryForm, setDeliveryForm] = useState({
    received_by: '',
    notes: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, statusFilter]);

  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/api/nas-customer/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setIsLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o =>
        o.delivery_code?.toLowerCase().includes(term) ||
        o.customer_name?.toLowerCase().includes(term)
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter);
    }
    
    setFilteredOrders(filtered);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Action handlers
  const handleApprove = async (order) => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/api/nas-customer/admin/orders/${order.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order approved!');
      loadOrders();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve order');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReject = async () => {
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/api/nas-customer/admin/orders/${selectedOrder.id}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Order rejected');
      setIsRejectOpen(false);
      setRejectReason('');
      loadOrders();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject order');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRelease = async (order) => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/api/nas-customer/admin/orders/${order.id}/release`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order released from bodega!');
      loadOrders();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to release order');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignDriver = async () => {
    if (!selectedDriver) {
      toast.error('Select a driver');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/api/nas-customer/admin/orders/${selectedOrder.id}/assign-driver?driver_name=${encodeURIComponent(selectedDriver)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Driver ${selectedDriver} assigned!`);
      setIsAssignDriverOpen(false);
      setSelectedDriver('');
      loadOrders();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign driver');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelivery = async () => {
    if (!deliveryForm.received_by) {
      toast.error('Enter receiver name');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/api/nas-customer/driver/confirm-delivery/${selectedOrder.id}`,
        {
          order_id: selectedOrder.id,
          received_by: deliveryForm.received_by,
          notes: deliveryForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Delivery confirmed!');
      setIsConfirmDeliveryOpen(false);
      setDeliveryForm({ received_by: '', notes: '' });
      loadOrders();
      setIsDetailOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm delivery');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRecordPayment = async () => {
    if (paymentForm.amount <= 0) {
      toast.error('Enter payment amount');
      return;
    }
    
    setIsSaving(true);
    try {
      await axios.post(
        `${API}/api/nas-customer/admin/orders/${selectedOrder.id}/payment`,
        {
          order_id: selectedOrder.id,
          ...paymentForm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment recorded!');
      setIsPaymentOpen(false);
      setPaymentForm({ amount: 0, payment_method: 'cash', reference: '', notes: '' });
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setIsSaving(false);
    }
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setIsDetailOpen(true);
  };

  // Stats
  const stats = {
    pending: orders.filter(o => o.status === 'pending').length,
    approved: orders.filter(o => o.status === 'approved').length,
    released: orders.filter(o => o.status === 'released').length,
    outForDelivery: orders.filter(o => o.status === 'out_for_delivery').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Order Approval</h1>
          <p className="text-slate-500">Manage and approve customer orders</p>
        </div>
        <Button variant="outline" onClick={loadOrders} disabled={isLoading}>
          <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-3">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
            <p className="text-xs text-amber-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{stats.approved}</p>
            <p className="text-xs text-blue-600">Approved</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-purple-700">{stats.released}</p>
            <p className="text-xs text-purple-600">Released</p>
          </CardContent>
        </Card>
        <Card className="bg-cyan-50 border-cyan-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-cyan-700">{stats.outForDelivery}</p>
            <p className="text-xs text-cyan-600">Out for Delivery</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
            <p className="text-xs text-green-600">Delivered</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            placeholder="Search by code or customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="released">Released</SelectItem>
            <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-green-600" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-10 text-center">
            <ClipboardCheck size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No orders found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredOrders.map(order => {
            const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            const remaining = order.total_amount - (order.amount_paid || 0);
            
            return (
              <Card key={order.id} className="bg-white">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Order Info */}
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${statusConfig.color}`}>
                        <StatusIcon size={24} />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{order.delivery_code}</p>
                        <p className="text-sm text-slate-500">{order.customer_name}</p>
                        <p className="text-xs text-slate-400">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                    
                    {/* Amount Info */}
                    <div className="text-center px-4">
                      <p className="text-lg font-bold text-green-600">₱{order.total_amount?.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">{order.items?.length} items</p>
                      {remaining > 0 && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300 mt-1">
                          Due: ₱{remaining.toLocaleString()}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Status & Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                      {order.auto_approved && (
                        <Badge className="bg-purple-100 text-purple-700 border border-purple-300">
                          <Sparkles size={12} className="mr-1" />
                          Auto-Approved
                        </Badge>
                      )}
                      
                      <Button variant="outline" size="sm" onClick={() => openOrderDetail(order)}>
                        <Eye size={16} className="mr-1" /> View
                      </Button>
                      
                      {/* Quick Actions based on status */}
                      {order.status === 'pending' && (
                        <>
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(order)}
                            data-testid={`approve-${order.id}`}
                          >
                            <ThumbsUp size={16} className="mr-1" /> Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => { setSelectedOrder(order); setIsRejectOpen(true); }}
                          >
                            <ThumbsDown size={16} className="mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      
                      {order.status === 'approved' && (
                        <Button 
                          size="sm" 
                          className="bg-purple-600 hover:bg-purple-700"
                          onClick={() => handleRelease(order)}
                        >
                          <Package size={16} className="mr-1" /> Release
                        </Button>
                      )}
                      
                      {order.status === 'released' && (
                        <Button 
                          size="sm" 
                          className="bg-cyan-600 hover:bg-cyan-700"
                          onClick={() => { setSelectedOrder(order); setIsAssignDriverOpen(true); }}
                        >
                          <Truck size={16} className="mr-1" /> Assign Driver
                        </Button>
                      )}
                      
                      {order.status === 'out_for_delivery' && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => { setSelectedOrder(order); setIsConfirmDeliveryOpen(true); }}
                        >
                          <CheckCircle size={16} className="mr-1" /> Confirm Delivery
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Order Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details - {selectedOrder?.delivery_code}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Customer & Status */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <User size={20} className="text-slate-400" />
                  <div>
                    <p className="font-semibold">{selectedOrder.customer_name}</p>
                    <p className="text-sm text-slate-500">{selectedOrder.customer_phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedOrder.auto_approved && (
                    <Badge className="bg-purple-100 text-purple-700 border border-purple-300">
                      <Sparkles size={12} className="mr-1" />
                      Auto-Approved
                    </Badge>
                  )}
                  <Badge className={STATUS_CONFIG[selectedOrder.status]?.color}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </Badge>
                </div>
              </div>
              
              {/* Delivery Address */}
              <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                <MapPin size={18} className="text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Delivery Address</p>
                  <p className="font-medium">{selectedOrder.delivery_address}</p>
                </div>
              </div>
              
              {/* Items */}
              <div>
                <h4 className="font-semibold mb-2">Order Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="text-left p-2">Product</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">Price</th>
                        <th className="text-right p-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{item.product_name}</td>
                          <td className="text-right p-2">{item.quantity} {item.unit}</td>
                          <td className="text-right p-2">₱{item.unit_price?.toLocaleString()}</td>
                          <td className="text-right p-2 font-medium">₱{item.total?.toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className="border-t bg-green-50">
                        <td colSpan={3} className="p-2 font-bold">Total Amount</td>
                        <td className="text-right p-2 font-bold text-green-600">₱{selectedOrder.total_amount?.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Payment Info */}
              <div className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Payment Status</p>
                  <p className="font-semibold">
                    Paid: ₱{(selectedOrder.amount_paid || 0).toLocaleString()} / ₱{selectedOrder.total_amount?.toLocaleString()}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { setPaymentForm({...paymentForm, amount: selectedOrder.total_amount - (selectedOrder.amount_paid || 0)}); setIsPaymentOpen(true); }}
                >
                  <DollarSign size={16} className="mr-1" /> Record Payment
                </Button>
              </div>
              
              {/* Notes */}
              {selectedOrder.notes && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-700">Customer Notes: {selectedOrder.notes}</p>
                </div>
              )}
              
              {/* Driver Info */}
              {selectedOrder.driver && (
                <div className="p-3 bg-cyan-50 rounded-lg">
                  <p className="text-sm text-cyan-700">Driver: {selectedOrder.driver}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>Close</Button>
            
            {selectedOrder?.status === 'pending' && (
              <>
                <Button 
                  className="bg-green-600" 
                  onClick={() => handleApprove(selectedOrder)}
                  disabled={isSaving}
                >
                  <ThumbsUp size={16} className="mr-1" /> Approve
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => setIsRejectOpen(true)}
                >
                  <ThumbsDown size={16} className="mr-1" /> Reject
                </Button>
              </>
            )}
            
            {selectedOrder?.status === 'approved' && (
              <Button 
                className="bg-purple-600" 
                onClick={() => handleRelease(selectedOrder)}
                disabled={isSaving}
              >
                <Package size={16} className="mr-1" /> Release from Bodega
              </Button>
            )}
            
            {selectedOrder?.status === 'released' && (
              <Button 
                className="bg-cyan-600"
                onClick={() => setIsAssignDriverOpen(true)}
              >
                <Truck size={16} className="mr-1" /> Assign Driver
              </Button>
            )}
            
            {selectedOrder?.status === 'out_for_delivery' && (
              <Button 
                className="bg-green-600"
                onClick={() => setIsConfirmDeliveryOpen(true)}
              >
                <CheckCircle size={16} className="mr-1" /> Confirm Delivery
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={isRejectOpen} onOpenChange={setIsRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-600">Reject order <strong>{selectedOrder?.delivery_code}</strong>?</p>
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter reason for rejection..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={isSaving}>
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : 'Reject Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Driver Modal */}
      <Dialog open={isAssignDriverOpen} onOpenChange={setIsAssignDriverOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Driver</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-600">Assign driver for order <strong>{selectedOrder?.delivery_code}</strong></p>
            <div>
              <Label>Select Driver</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a driver..." />
                </SelectTrigger>
                <SelectContent>
                  {DRIVERS.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAssignDriverOpen(false)}>Cancel</Button>
            <Button className="bg-cyan-600" onClick={handleAssignDriver} disabled={isSaving}>
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : 'Assign Driver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delivery Modal */}
      <Dialog open={isConfirmDeliveryOpen} onOpenChange={setIsConfirmDeliveryOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delivery</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-600">Confirm delivery for order <strong>{selectedOrder?.delivery_code}</strong></p>
            <div>
              <Label>Received By *</Label>
              <Input
                value={deliveryForm.received_by}
                onChange={(e) => setDeliveryForm({...deliveryForm, received_by: e.target.value})}
                placeholder="Name of person who received"
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={deliveryForm.notes}
                onChange={(e) => setDeliveryForm({...deliveryForm, notes: e.target.value})}
                placeholder="Delivery notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfirmDeliveryOpen(false)}>Cancel</Button>
            <Button className="bg-green-600" onClick={handleConfirmDelivery} disabled={isSaving}>
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : 'Confirm Delivery'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={isPaymentOpen} onOpenChange={setIsPaymentOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₱) *</Label>
              <Input
                type="number"
                value={paymentForm.amount || ''}
                onChange={(e) => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})}
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="gcash">GCash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference Number</Label>
              <Input
                value={paymentForm.reference}
                onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})}
                placeholder="GCash ref, check number..."
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPaymentOpen(false)}>Cancel</Button>
            <Button className="bg-green-600" onClick={handleRecordPayment} disabled={isSaving}>
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NASOrderApproval;
